import rawTokens from '../config/design-tokens.json'

type TokenValue = string | number

type TokenNode = TokenValue | { [key: string]: TokenNode }

export type DesignTokens = typeof rawTokens

export type SimpleTokenItem = {
  name: string
  path: string
  value: string
}

export type TypographyMeta = {
  fontFamily: string
  fontSize: string
  fontWeight: number
  lineHeight: string
  letterSpacing?: string
}

export type TypographyTokenItem = {
  name: string
  path: string
  value: string
  meta: TypographyMeta
}

const toTokenName = (path: string) => {
  const segments = path.split('.')
  return segments[segments.length - 1] ?? path
}

const flattenTokenValues = (node: TokenNode, prefix: string): SimpleTokenItem[] => {
  if (typeof node === 'string' || typeof node === 'number') {
    return [{ name: toTokenName(prefix), path: prefix, value: String(node) }]
  }

  return Object.entries(node).flatMap(([key, value]) =>
    flattenTokenValues(value, `${prefix}.${key}`)
  )
}

const typographyTokenValue = (token: TypographyMeta) => {
  const base = `${token.fontSize} / ${token.lineHeight} / ${token.fontWeight}`
  if (token.letterSpacing) {
    return `${base} / ${token.letterSpacing}`
  }
  return base
}

type TypographyToken = {
  size: string
  lineHeight: string
  weight: number
  letterSpacing?: string
}

const isTypographyToken = (value: unknown): value is TypographyToken => {
  if (!value || typeof value !== 'object') {
    return false
  }
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.size === 'string' &&
    typeof candidate.lineHeight === 'string' &&
    typeof candidate.weight === 'number'
  )
}

export const designTokens = rawTokens

export const colorTokens = flattenTokenValues(designTokens.colors, 'colors')

export const spacingTokens = flattenTokenValues(designTokens.spacing, 'spacing')

export const shadowTokens = flattenTokenValues(designTokens.shadows, 'shadows')

export const sizeTokens = flattenTokenValues(designTokens.sizes, 'sizes')

export const radiusTokens = flattenTokenValues(designTokens.borders.radius, 'borders.radius')

export const borderWidthTokens = flattenTokenValues(designTokens.borders.widths, 'borders.widths')

export const borderStyleTokens = flattenTokenValues(designTokens.borders.styles, 'borders.styles')

export const typographyTokens = (() => {
  const { typography } = designTokens
  const fontFamilies = typography.fontFamilies
  const primaryFontFamily = fontFamilies.primary
  const mobileFontFamily = fontFamilies.mobileSystem
  const adminFontFamily = fontFamilies.adminSecondary

  const tokens: TypographyTokenItem[] = []

  Object.entries(fontFamilies).forEach(([key, value]) => {
    const path = `typography.fontFamilies.${key}`
    const meta: TypographyMeta = {
      fontFamily: value,
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '1.4',
    }

    tokens.push({
      name: key,
      path,
      value: value,
      meta,
    })
  })

  Object.entries(typography).forEach(([groupKey, groupValue]) => {
    if (groupKey === 'fontFamilies') {
      return
    }

    const resolvedFontFamily =
      groupKey === 'mobile'
        ? mobileFontFamily
        : groupKey === 'admin'
          ? adminFontFamily
          : primaryFontFamily

    if (isTypographyToken(groupValue)) {
      const path = `typography.${groupKey}`
      const meta: TypographyMeta = {
        fontFamily: resolvedFontFamily,
        fontSize: groupValue.size,
        fontWeight: groupValue.weight,
        lineHeight: groupValue.lineHeight,
        letterSpacing: groupValue.letterSpacing,
      }
      tokens.push({
        name: groupKey,
        path,
        value: typographyTokenValue(meta),
        meta,
      })
      return
    }

    if (!groupValue || typeof groupValue !== 'object') {
      return
    }

    Object.entries(groupValue).forEach(([tokenKey, tokenValue]) => {
      if (!isTypographyToken(tokenValue)) {
        return
      }
      const path = `typography.${groupKey}.${tokenKey}`
      const meta: TypographyMeta = {
        fontFamily: resolvedFontFamily,
        fontSize: tokenValue.size,
        fontWeight: tokenValue.weight,
        lineHeight: tokenValue.lineHeight,
        letterSpacing: tokenValue.letterSpacing,
      }
      tokens.push({
        name: tokenKey,
        path,
        value: typographyTokenValue(meta),
        meta,
      })
    })
  })

  return tokens
})()

export const effectTokens = flattenTokenValues(designTokens.effects, 'effects')
